import { useState } from 'react'
import { Box, Button, TextField, Typography } from '@mui/material'

import { submitSurvey } from '../../../services/api/OnboardingDataService'
import { USER_TYPES, REFERRAL_SOURCES } from '../onboardingContent'
import { onboardingStyles as s, ACCENT } from '../onboardingStyles'

function OptionGrid({ options, selected, onSelect }) {
    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            {options.map((opt) => {
                const isSelected = selected === opt.key
                return (
                    <Button
                        key={opt.key}
                        onClick={() => onSelect(opt.key)}
                        disableElevation
                        sx={{
                            textTransform: 'none',
                            fontFamily: 'Catamaran',
                            fontWeight: 600,
                            borderRadius: '999px',
                            px: 2,
                            py: 0.8,
                            border: isSelected ? `2px solid ${ACCENT}` : '2px solid #e0e0e0',
                            backgroundColor: isSelected ? 'rgba(255,20,147,0.08)' : '#fff',
                            color: isSelected ? ACCENT : 'text.gray',
                            '&:hover': { backgroundColor: isSelected ? 'rgba(255,20,147,0.12)' : '#f7f7f7' },
                        }}
                    >
                        {opt.label}
                    </Button>
                )
            })}
        </Box>
    )
}

export default function AboutYouStep({ setUserType, goNext }) {
    const [userTypeKey, setUserTypeKey] = useState(null)
    const [userTypeOther, setUserTypeOther] = useState('')
    const [referralKey, setReferralKey] = useState(null)
    const [referralOther, setReferralOther] = useState('')
    const [submitting, setSubmitting] = useState(false)

    const canContinue = Boolean(userTypeKey) && (userTypeKey !== 'other' || userTypeOther.trim().length > 0)

    const handleContinue = async () => {
        setSubmitting(true)
        setUserType(userTypeKey) // tailors copy in later steps
        // Persist the survey to the CSV; don't block onboarding on it (returns false on failure).
        await submitSurvey({
            userType: userTypeKey || '',
            userTypeOther: userTypeKey === 'other' ? userTypeOther.trim() : '',
            referralSource: referralKey || '',
            referralSourceOther: referralKey === 'other' ? referralOther.trim() : '',
        })
        goNext()
    }

    return (
        <Box>
            <Typography sx={s.title}>Tell us about you</Typography>
            <Typography sx={s.subtitle}>
                So we can show you what Vibio can do for <em>you</em>.
            </Typography>

            <Typography sx={{ fontFamily: 'Catamaran', fontWeight: 700, mb: 1 }}>
                What do you do?
            </Typography>
            <OptionGrid options={USER_TYPES} selected={userTypeKey} onSelect={setUserTypeKey} />
            {userTypeKey === 'other' && (
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Tell us what you do"
                    value={userTypeOther}
                    onChange={(e) => setUserTypeOther(e.target.value)}
                    sx={{ mt: 1, mb: 1 }}
                />
            )}

            <Typography sx={{ fontFamily: 'Catamaran', fontWeight: 700, mt: 2.5, mb: 1 }}>
                How did you hear about Vibio? <span style={{ color: '#9aa3b2', fontWeight: 500 }}>(optional)</span>
            </Typography>
            <OptionGrid options={REFERRAL_SOURCES} selected={referralKey} onSelect={setReferralKey} />
            {referralKey === 'other' && (
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Where did you hear about us?"
                    value={referralOther}
                    onChange={(e) => setReferralOther(e.target.value)}
                    sx={{ mt: 1 }}
                />
            )}

            <Box sx={s.footer}>
                <Button
                    variant="contained"
                    disableElevation
                    disabled={!canContinue || submitting}
                    onClick={handleContinue}
                    sx={s.ctaButton}
                >
                    {submitting ? 'Saving…' : 'Continue'}
                </Button>
            </Box>
        </Box>
    )
}
